import React, { useCallback, useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import { browsePagerIconSx } from "../../dark-theme/surfaces";

const TAG_SCROLL_EDGE_PX = 4;
const TAG_SCROLL_STEP_PX = 200;

/**
 * Single-row tag chips with left/right arrows when the list overflows.
 * Vertical mouse wheel scrolls the row sideways.
 */
export default function BrowseTagScrollRow({
    children,
    ariaLabel,
    scrollLeftLabel,
    scrollRightLabel,
}) {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateArrows = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const overflow = scrollWidth > clientWidth + TAG_SCROLL_EDGE_PX;
        setCanScrollLeft(overflow && scrollLeft > TAG_SCROLL_EDGE_PX);
        setCanScrollRight(
            overflow && scrollLeft + clientWidth < scrollWidth - TAG_SCROLL_EDGE_PX,
        );
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        const sync = () => updateArrows();
        sync();
        const raf = requestAnimationFrame(sync);
        el.addEventListener("scroll", sync, { passive: true });
        const onWheel = (event) => {
            if (el.scrollWidth <= el.clientWidth + TAG_SCROLL_EDGE_PX) return;
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            event.preventDefault();
            el.scrollLeft += event.deltaY;
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        window.addEventListener("resize", sync);

        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener("scroll", sync);
            el.removeEventListener("wheel", onWheel);
            window.removeEventListener("resize", sync);
            ro.disconnect();
        };
    }, [updateArrows, children]);

    const scrollByDir = (direction) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * TAG_SCROLL_STEP_PX, behavior: "smooth" });
        window.setTimeout(updateArrows, 350);
        window.setTimeout(updateArrows, 550);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 2.5,
                minWidth: 0,
            }}
        >
            {canScrollLeft ? (
                <IconButton
                    size="small"
                    aria-label={scrollLeftLabel}
                    onClick={() => scrollByDir(-1)}
                    sx={browsePagerIconSx}
                >
                    <ChevronLeftIcon />
                </IconButton>
            ) : null}
            <Box
                ref={scrollRef}
                role="tablist"
                aria-label={ariaLabel}
                sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: 0,
                    overflowX: "auto",
                    overflowY: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {children}
            </Box>
            {canScrollRight ? (
                <IconButton
                    size="small"
                    aria-label={scrollRightLabel}
                    onClick={() => scrollByDir(1)}
                    sx={browsePagerIconSx}
                >
                    <ChevronRightIcon />
                </IconButton>
            ) : null}
        </Box>
    );
}
